/**
 * RefreshButton — inline refresh control for dashboard sections.
 *
 * Shows a rotating icon while data is loading and prints the last
 * refresh timestamp so the user knows how stale the data is.
 */
import React from 'react';
import { IconButton, Tooltip, Typography, Box } from '@mui/material';
import { Refresh } from '@mui/icons-material';

interface RefreshButtonProps {
  /** Trigger a refresh of the parent section's data. */
  onRefresh: () => void;
  /** Whether data is currently being fetched. */
  loading?: boolean;
  /** Timestamp of the last successful refresh. */
  lastRefreshed?: Date | null;
  /** Optional size variant. */
  size?: 'small' | 'medium';
}

const RefreshButton: React.FC<RefreshButtonProps> = ({
  onRefresh,
  loading = false,
  lastRefreshed = null,
  size = 'small',
}) => {
  const formattedTime = lastRefreshed
    ? lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={loading ? 'Refreshing…' : 'Refresh'}>
        <span>
          <IconButton
            onClick={onRefresh}
            disabled={loading}
            size={size}
            sx={{
              transition: 'transform 0.3s',
              ...(loading && {
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }),
            }}
          >
            <Refresh fontSize={size} />
          </IconButton>
        </span>
      </Tooltip>
      {formattedTime && !loading && (
        <Typography variant="caption" color="text.secondary">
          Updated {formattedTime}
        </Typography>
      )}
    </Box>
  );
};

export default RefreshButton;
